"use client";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "./ui";
import type { OrderOutput } from "@/api/online-ordering-service/model";

interface ReviewPromptModalProps {
  order: OrderOutput | null;
  /** Called on EITHER the CTA or the dismiss action — caller marks the order prompted. */
  onDismiss: () => void;
}

/**
 * App-level "how was your order?" auto-prompt. Distinct from
 * OrderDetailScreen's auto-navigate: this is a modal, not a redirect, because
 * the customer may have opened the app to reorder rather than to review —
 * hijacking them straight to the review screen would be hostile. Shown at
 * most once per order (src/lib/reviewPrompt.ts gate), triggered by
 * useReviewPromptTrigger on app open/resume regardless of which screen is
 * currently active.
 */
export function ReviewPromptModal({ order, onDismiss }: ReviewPromptModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!order) return null;

  function handleReview() {
    onDismiss();
    navigate(`/orders/${order!.id}/review`);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("v2.reviews.prompt_modal.title", "How was your order?")}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onDismiss}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 500,
          background: "var(--surface-1)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.15)",
          padding: "16px 18px calc(env(safe-area-inset-bottom, 0px) + 22px)",
          animation: "review-prompt-sheet-up 300ms cubic-bezier(0.32, 0.72, 0, 1)",
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-2-strong)",
            margin: "2px auto 16px",
          }}
        />

        <div style={{ textAlign: "center", marginBottom: 8, fontSize: 32 }} aria-hidden="true">
          ⭐️
        </div>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div className="display" style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)" }}>
            {t("v2.reviews.prompt_modal.title", "How was your order?")}
          </div>
          <div style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 6, lineHeight: 1.4 }}>
            {t(
              "v2.reviews.prompt_modal.subtitle",
              "Your feedback takes 10 seconds and helps the team do better.",
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Button full size="lg" onClick={handleReview}>
            {t("v2.reviews.prompt_modal.cta", "Leave a review")}
          </Button>
          <Button full variant="ghost" testId="review-later" onClick={onDismiss}>
            {t("v2.reviews.maybe_later", "Maybe later")}
          </Button>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes review-prompt-sheet-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `,
        }}
      />
    </div>
  );
}
